import { useNotificationContext } from '@/contexts/NotificationContext';
import { Alert, AlertColor, Box, Slide } from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

const NotificationContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  flexDirection: 'column-reverse',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  zIndex: theme.zIndex.snackbar,
  pointerEvents: 'none',
  maxWidth: '100%',
  width: 'auto',
  '& > *': {
    pointerEvents: 'auto',
  },
}));

const NotificationItem = styled(Box)(() => ({
  minWidth: 300,
  maxWidth: '90vw',
  width: '100%',
}));

const NotificationManager = () => {
  const { notifications, removeNotification } = useNotificationContext();
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  useEffect(() => {
    const currentIds = new Set(notifications.map((n) => n.id));
    timersRef.current.forEach((timer, id) => {
      if (!currentIds.has(id)) {
        clearTimeout(timer);
        timersRef.current.delete(id);
      }
    });
  }, [notifications]);

  const handleClose = useCallback(
    (notificationId: string) => () => {
      removeNotification(notificationId);
      const timer = timersRef.current.get(notificationId);
      if (timer) {
        clearTimeout(timer);
        timersRef.current.delete(notificationId);
      }
    },
    [removeNotification],
  );

  const getSeverity = (type: string): AlertColor => {
    switch (type) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      default:
        return 'info';
    }
  };

  const getDuration = (notification: typeof notifications[0]): number => {
    if (notification.duration !== undefined) {
      return notification.duration;
    }
    switch (notification.type) {
      case 'error':
        return 6000;
      case 'warning':
        return 4000;
      case 'success':
        return 3000;
      default:
        return 3000;
    }
  };

  useEffect(() => {
    notifications.forEach((notification) => {
      if (timersRef.current.has(notification.id)) {
        return;
      }

      const duration = getDuration(notification);
      const timer = setTimeout(() => {
        removeNotification(notification.id);
        timersRef.current.delete(notification.id);
      }, duration);

      timersRef.current.set(notification.id, timer);
    });
  }, [notifications, removeNotification]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, []);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <NotificationContainer>
      {notifications.map((notification) => (
        <Slide
          key={notification.id}
          direction="up"
          in={true}
          mountOnEnter
          unmountOnExit
        >
          <NotificationItem>
            <Alert
              onClose={handleClose(notification.id)}
              severity={getSeverity(notification.type)}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notification.message}
            </Alert>
          </NotificationItem>
        </Slide>
      ))}
    </NotificationContainer>
  );
};

export default NotificationManager;
